import { Rank, type EnemyDef } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/* ═══════════════════════════════════════════════════════════════════════════
   EB-1-g/d — LES DEUX CANONS DU CHAR.

   Un seul fichier pour les deux, comme les tourelles du Bastion : ce qui compte
   est le CONTRASTE entre eux — un canon visé, un canon d'aire — et ce contraste
   se lit mal éclaté sur deux fichiers.

   ── Pourquoi deux natures et non deux copies ──
   Le Char verrouille son noyau : abattre les deux canons est OBLIGATOIRE, pas
   optionnel. Si l'obligation ne portait que sur des canons identiques, l'ordre
   de destruction n'aurait aucune conséquence et le verrou dégénérerait en simple
   barre de points de vie à raboter. En donnant à chaque canon une menace
   distincte, on fait de l'ordre un vrai choix : lequel des deux gênes-tu le
   plus, et donc lequel fais-tu taire en premier.

   ⚠️ 300 PV chacun (600 pour désarmer) contre 500 pour le châssis. Le total
   dépasse le noyau, mais ici la comparaison ne sert pas à arbitrer : on n'a pas
   le droit d'ignorer les canons. Elle sert à DOSER — 600 PV de désarmement
   forcé, c'est un combat qui dure sans s'éterniser, calibré pour que la leçon de
   priorité ait le temps de se jouer une fois, pas trois.
   ═══════════════════════════════════════════════════════════════════════════ */

const BASE = {
  rank: Rank.Standard,
  hp: 300,
  visualRadius: 17,
  score: 240,
  telegraph: 1.1,
  drops: 1,
  // Repositionnée chaque tick sur son porteur : sa trajectoire propre n'est
  // jamais lue. Anchor inerte pour que la lecture du fichier ne suggère rien.
  motion: { kind: Motion.Anchor, speed: 0, holdY: 300 },
} as const;

const canons: EnemyDef[] = [
  defineEnemy({
    ...BASE,
    id: 'char-tourelle-g',
    emitter: {
      // Le canon VISÉ : il suit le joueur. C'est lui qu'on fait taire quand on
      // veut regagner le droit de s'immobiliser.
      pattern: Pattern.Aimed,
      every: 1.9,
      count: 2,
      spreadDeg: 14,
      speed: 440,
      firstDelay: 1.5,
      family: Fam.EnemyPink,
    },
  }),
  defineEnemy({
    ...BASE,
    id: 'char-tourelle-d',
    emitter: {
      // Le canon d'AIRE : un éventail statique qui condamne un pan de terrain
      // sans se soucier du joueur. Décalé de 0,5 s du visé pour que les deux ne
      // produisent pas une salve unique mais une pression continue.
      pattern: Pattern.Fan,
      every: 1.9,
      count: 5,
      spreadDeg: 58,
      speed: 360,
      firstDelay: 2.0,
      family: Fam.EnemyPurple,
    },
  }),
];

export default canons;
